/**
 * entity_search — GUI system command schema.
 *
 * Used by: gui/src/systemCommands/entitySystem/entity_search.ts
 * Backend: GET /api/entities/search (entity-level text search + optional vector re-rank)
 *
 * NOTE: The AI tool-runtime uses a DIFFERENT schema (mode instead of semantic) and a
 * different backend endpoint (POST /api/search/universal — full 4-stage pipeline).
 * See tool-runtime/src/tools/entity_search.ts for the AI-facing definition.
 *
 * ROUTING GUIDANCE FOR THE AI:
 *   entity_find  → navigate by known name or type
 *                  (you know the entity is called "Q4 Report" or want all Tasks)
 *   entity_search → discover by content or meaning
 *                  (you want everything about "enterprise customers", "platform migration",
 *                   or "what did I write about competitor pricing")
 *
 * entity_search searches extracted content fields (notes, description, tags, topics, etc.)
 * in addition to names, using a full-text index. With semantic=true it also applies
 * vector similarity re-ranking for conceptual matches.
 */

export const MANIFEST = {
  name: 'entity_search',
  description: `Search the entire DATASPACE by content or meaning — not just by name.

**SCOPE:** Searches across ALL content in the dataspace regardless of which workspace or folder it's in. This is the right tool for discovery.

**WHEN TO USE THIS TOOL:**
Use entity_search when you want to find entities based on what they *contain* or *mean*:
- "Find everything I have about enterprise sales" → entity_search
- "What did I write about competitor pricing?" → entity_search
- "Find contacts at companies in the healthcare space" → entity_search
- "What tasks mention the Q4 product launch?" → entity_search
- Any topic-based search where exact name is unknown

**WHEN TO USE entity_find INSTEAD:**
Use entity_find when you know the approximate name or type:
- "Find the document called Q4 Report" → entity_find
- "List all my tasks" → entity_find (entityType: Task)
- "Find the contact Alice Johnson" → entity_find (searchQuery: "Alice Johnson")

**IF RESULTS ARE EMPTY:** The content genuinely doesn't exist in the dataspace. Do NOT use space_tree, context_discover, or other space-scoped tools — those only show a single workspace window and won't find content in other spaces or folders.

**RETURNS:** Array of { entity_id, name, entityType, score, snippet } objects sorted by relevance.
Use entity_id values to fetch or open specific entities.`,

  inputSchema: {
    type: 'object' as const,
    properties: {
      query: {
        type: 'string',
        description: 'What to search for — a topic, concept, phrase, or question.',
      },
      entityTypes: {
        type: 'array',
        items: { type: 'string' },
        description: 'Optional: restrict to specific entity types (e.g. ["Contact", "Task"])',
      },
      semantic: {
        type: 'boolean',
        description: 'When true, also applies vector similarity for conceptual matches. Default false.',
        default: false,
      },
      limit: {
        type: 'number',
        description: 'Maximum results to return (default 20, max 50)',
        default: 20,
      },
    },
    required: ['query'],
  },
} as const;
